import { useEffect, useRef, useState } from 'react';
import { tableHeader } from '../data';
import styles from './TableModule.module.scss';
import arrowIcon from '@assets/img/UI/arrowMini.svg';
import DirectionList from './DirectionList';
import { AnimatePresence } from 'framer-motion';
import trash from '@assets/img/UI/trash.svg';
import ModalDeleteReportAdmin from '../../../../components/ModalDeleteReportAdmin/ModalDeleteReportAdmin';
import {
  apiDeleteMulti,
  server,
  uploadPhoto,
} from '../../../../apirequests/apirequests';
import { ReactComponent as UploadIcon } from '@assets/img/AdminPanel/fileDownloadOutline.svg';
import fileIcon from '@assets/img/AdminPanel/fileSolid.svg';
import closeIcon from '@assets/img/AdminPanel/closeX.svg';
import ErrorModal from '../../../../components/ErrorModal/ErrorModal';
import { motion } from 'framer-motion';

const ALLOWED_EXTENSIONS = ['.pptx', '.ppt', '.pdf'];
const MAX_SIZE_BYTES = 260 * 1024 * 1024;
const ERROR_FORMAT = 'Допустимый формат файла: .pptx, .ppt, .pdf';
const ERROR_SIZE = 'Максимальный размер файла - 260 Мб';
const ERROR_UPLOAD = 'Не удалось загрузить презентацию. Попробуйте ещё раз.';

const getPresentationUploadError = errorLike => {
  const data = errorLike?.response?.data || errorLike?.data;
  const errCode = data?.errCode;
  const message = data?.message || '';

  if (
    errCode === 'FileTooLarge' ||
    /file too large|слишком больш|размер/i.test(message)
  ) {
    return ERROR_SIZE;
  }
  if (
    errCode === 'FileExtensionNotAllowed' ||
    /extension|формат|not allowed/i.test(message)
  ) {
    return ERROR_FORMAT;
  }
  return ERROR_UPLOAD;
};

function TableModule({
  prewData,
  tableData,
  setTableData,
  direction,
  setOriginalData,
  originalData,
}) {
  const [openDirList, setOpenDirList] = useState(null);
  const [posDirList, setPosDirList] = useState({ x: 0, y: 0 });
  const listRef = useRef(null);
  const listModalRef = useRef(null);
  const [modalDelete, setModalDelete] = useState(false);
  const [errorModal, setErrorModal] = useState('');
  const [deletePresentation, setDeletePresentation] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRefs = useRef({});

  const funOpenDirList = (e, index) => {
    e.stopPropagation();
    const clickX = e.clientX;
    const clickY = e.clientY;
    let posY = clickY + 20;
    let posX = clickX + 50;
    if (posY + 300 > window.innerHeight) {
      posY = window.innerHeight - 300;
    }
    setPosDirList({ x: posX, y: posY });
    setOpenDirList(prev => (prev === index ? null : index));
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        listRef.current &&
        !listRef.current.contains(event.target) &&
        listModalRef.current &&
        !listModalRef.current.contains(event.target)
      ) {
        setOpenDirList(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const funSetDirection = (value, indexRow) => {
    let dat = [...tableData];
    dat[indexRow].direction = value.name;
    setTableData(dat);
  };

  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(`${server}/${fileUrl}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const getFileExtension = fileUrl => {
    const clearUrl = fileUrl?.split('?')[0] || '';
    const currentFileName = clearUrl.split('/').pop() || '';
    const dotIndex = currentFileName.lastIndexOf('.');

    return dotIndex >= 0 ? currentFileName.slice(dotIndex) : '';
  };

  const updatePresentationInState = (reportId, presentation) => {
    const patch = rows =>
      rows.map(row => (row.id === reportId ? { ...row, presentation } : row));
    setTableData(prev => patch(prev));
    setOriginalData(prev => patch(prev));
  };

  const handlePresentationSelect = async (event, reportId) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;

    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setErrorModal(ERROR_FORMAT);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setErrorModal(ERROR_SIZE);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('reportId', reportId);
    setUploadingId(reportId);
    try {
      const res = await uploadPhoto(formData, 'PRESENTATION_REPORT');
      if (res?.status === 200 && res?.data?.file) {
        const uploaded = res.data.file;
        updatePresentationInState(reportId, [
          { id: uploaded.id, url: uploaded.url, name: uploaded.name },
        ]);
        return;
      }

      setErrorModal(getPresentationUploadError(res));
    } catch (error) {
      setErrorModal(getPresentationUploadError(error));
    } finally {
      setUploadingId(null);
    }
  };

  const confirmDeletePresentation = async () => {
    if (!deletePresentation) return;
    const { reportId, fileId } = deletePresentation;
    const res = await apiDeleteMulti({ ids: [fileId] });
    if (res?.status === 200) {
      updatePresentationInState(reportId, []);
    }
    setDeletePresentation(null);
  };

  const getTdData = (key, row) => {
    if (key === 'fio') {
      return row[key].map((el, index) => <p key={index}>{el}</p>);
    }
    if (key === 'name') {
      const url = row.file?.[0]?.url;
      return (
        <div className={url ? styles.reportFileLink : ''}>
          {url ? (
            <a
              href={`${server}/${url}`}
              target="_blank"
              rel="noreferrer"
              onClick={e => {
                e.preventDefault();
                const authorByRole = row?.participantsWithRole?.find(
                  item => item?.who === 'Автор',
                )?.fio;
                const author =
                  authorByRole || row?.authorFio || (Array.isArray(row.fio) ? row.fio[0] : row.fio);
                const parts = author?.split(' ') || [];
                const fileExtension = getFileExtension(url);
                const fileName = `${row.name.split(' ').join('_')}_${parts[0] || ''}${
                  parts[1] ? `_${parts[1]?.[0]}` : ''
                }${parts[2] ? `_${parts[2]?.[0]}` : ''}${fileExtension}`;
                handleDownload(url, fileName);
              }}
            >
              {row[key]}
            </a>
          ) : (
            row[key]
          )}
        </div>
      );
    }
    if (key === 'presentation') {
      const presentation = row.presentation?.[0];
      if (presentation?.url) {
        return (
          <div className={styles.presentationCell}>
            <div className={styles.presentationChip}>
              <img src={fileIcon} alt="" className={styles.presentationFileIcon} />
              <a
                href={`${server}/${presentation.url}`}
                className={styles.presentationName}
                onClick={e => {
                  e.preventDefault();
                  handleDownload(presentation.url, presentation.name || 'presentation');
                }}
              >
                {presentation.name || 'Презентация'}
              </a>
              <button
                type="button"
                className={styles.presentationDelete}
                title="Удалить презентацию"
                onClick={() =>
                  setDeletePresentation({ reportId: row.id, fileId: presentation.id })
                }
              >
                <img src={closeIcon} alt="Удалить" />
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className={styles.presentationCell}>
          <input
            type="file"
            accept=".pptx,.ppt,.pdf,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            ref={el => {
              fileInputRefs.current[row.id] = el;
            }}
            style={{ display: 'none' }}
            onChange={e => handlePresentationSelect(e, row.id)}
          />
          <button
            type="button"
            className={styles.uploadPresentation}
            disabled={uploadingId === row.id}
            onClick={() => fileInputRefs.current[row.id]?.click()}
          >
            <UploadIcon className={styles.uploadPresentationIcon} aria-hidden />
            <span>{uploadingId === row.id ? 'Загрузка…' : 'Загрузить'}</span>
          </button>
        </div>
      );
    }
    if (key === 'action') {
      return (
        <button
          className={styles.trash}
          onClick={() => {
            setModalDelete(row);
          }}
          title="Удалить доклад"
        >
          <img src={trash} alt="Удалить" />
        </button>
      );
    }

    return row[key];
  };

  useEffect(() => {
    const handleScroll = () => {
      setOpenDirList(null);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <ModalDeleteReportAdmin
        data={modalDelete}
        setData={setModalDelete}
        setOriginalData={setOriginalData}
        originalData={originalData}
      />
      <ErrorModal title={errorModal} open={!!errorModal} close={() => setErrorModal('')} />
      <AnimatePresence>
        {deletePresentation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={styles.confirmBlur}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <p className={styles.confirmTitle}>Удалить презентацию?</p>
              <div className={styles.confirmBtns}>
                <button type="button" onClick={() => setDeletePresentation(null)}>
                  Отмена
                </button>
                <button type="button" onClick={confirmDeletePresentation}>
                  Удалить
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className={styles.TableModule} onScroll={() => setOpenDirList(null)}>
        <AnimatePresence>
          {openDirList !== null && (
            <DirectionList
              listModalRef={listModalRef}
              posDirList={posDirList}
              indexRow={openDirList}
              data={direction}
              selected={tableData?.[openDirList]?.direction}
              funSetDirection={funSetDirection}
            />
          )}
        </AnimatePresence>
        <table>
          <thead>
            <tr>
              {tableHeader.map((el, index) => (
                <th key={index}>{el.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData?.map((row, indexRow) => (
              <tr key={indexRow}>
                <td>{indexRow + 1}</td>
                {tableHeader.slice(1).map((columnKey, indexCol) =>
                  columnKey.key === 'direction' ? (
                    <td
                      ref={openDirList === indexRow ? listRef : null}
                      key={indexCol}
                      onClick={e => funOpenDirList(e, indexRow)}
                      style={{ cursor: 'pointer' }}
                      className={
                        prewData.find(el => el.id === row.id)?.direction !== row.direction
                          ? styles.editData
                          : ''
                      }
                    >
                      <div className={styles.direction_container}>
                        <div className={styles.left_box}>{row[columnKey.key]}</div>
                        <div className={styles.rigth_box}>
                          <img
                            src={arrowIcon}
                            alt="⬇️"
                            className={`${openDirList === indexRow ? styles.open : ''}`}
                          />
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td
                      key={indexCol}
                      className={
                        columnKey.key === 'presentation' ? styles.presentationTd : undefined
                      }
                    >
                      {getTdData(columnKey.key, row)}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default TableModule;
